/**
 * Solar report view — route-mounted daily/weekly/monthly/yearly solar report.
 *
 * The four cadences are the same page shell around SolarReportGrid; only the
 * period, default end date, data source, and export/title strings change.
 * SOLAR_PERIOD_CONFIG carries that per-cadence difference so the route table
 * can mount one component four times instead of four near-identical files.
 */
import { Box } from '@mui/material';
import { addDays, addMonths, addYears, endOfDay, endOfMonth, endOfYear, format, startOfWeek } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useScope } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { fetchAnnualSolarReportsForSiteGroupWithEndDate } from '@sophocles/entities/AnnualSolarReport';
import { fetchDailySolarReportsForSiteGroupWithEndDate } from '@sophocles/entities/DailySolarReport';
import { fetchMonthlySolarReportsForSiteGroupWithEndDate } from '@sophocles/entities/MonthlySolarReport';
import { fetchWeeklySolarReportsForSiteGroupWithEndDate } from '@sophocles/entities/WeeklySolarReport';

import SolarReportGrid from './components/SolarReportGrid';

const SOLAR_PERIOD_CONFIG = {
  day: {
    period: 'day',
    getInitialDate: () => format(endOfDay(addDays(new Date(), -1)), 'yyyy-MM-dd'),
    fetchAction: fetchDailySolarReportsForSiteGroupWithEndDate,
    fileNamePrefix: 'daily-solar-report',
    title: 'Daily Solar Report',
    titleKey: 'reports.dailySolar',
  },
  week: {
    period: 'week',
    getInitialDate: () => format(startOfWeek(addDays(new Date(), -1)), 'yyyy-MM-dd'),
    fetchAction: fetchWeeklySolarReportsForSiteGroupWithEndDate,
    fileNamePrefix: 'weekly-solar-report',
    title: 'Weekly Solar Report',
    titleKey: 'reports.weeklySolar',
  },
  month: {
    period: 'month',
    getInitialDate: () => format(endOfMonth(addMonths(new Date(), -1)), 'yyyy-MM-dd'),
    fetchAction: fetchMonthlySolarReportsForSiteGroupWithEndDate,
    fileNamePrefix: 'monthly-solar-report',
    title: 'Monthly Solar Report',
    titleKey: 'reports.monthlySolar',
  },
  year: {
    period: 'year',
    getInitialDate: () => format(endOfYear(addYears(new Date(), -1)), 'yyyy-MM-dd'),
    fetchAction: fetchAnnualSolarReportsForSiteGroupWithEndDate,
    fileNamePrefix: 'yearly-solar-report',
    title: 'Yearly Solar Report',
    titleKey: 'reports.yearlySolar',
  },
};

function SolarReportView({ period, ...props }) {
  const { t } = useTranslation();
  const {
    match: { params },
  } = props;

  const { customer, siteGroup } = useScope();
  const isBillboard = customer?.is_billboard ?? true;
  const config = SOLAR_PERIOD_CONFIG[period];
  const baseStructureLink = `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/`;

  return (
    <AppNav flex notFullHeight page="plant" {...props}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          padding: '0 var(--page-px) var(--page-py)',
        }}
      >
        <TitleBar showBackButton title={t(config.titleKey)} />
        <SolarReportGrid
          period={config.period}
          initialDate={config.getInitialDate()}
          fetchAction={config.fetchAction}
          fileNamePrefix={config.fileNamePrefix}
          title={config.title}
          baseStructureLink={baseStructureLink}
          isBillboard={isBillboard}
          siteGroupId={siteGroup?.id}
          siteGroup={siteGroup}
        />
      </Box>
    </AppNav>
  );
}

export default SolarReportView;
